import React, { useState } from "react"
import { container } from "../../infrastructure/container"
import { AppFranchiseService } from "../../infrastructure/services/api/franchise"
import { Logger } from "../../infrastructure/services/logger"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../presentation/context/AuthContext"
import { validatePassword } from "../../infrastructure/utils/validation"
import { TYPES } from "../../presentation/models/types"
import { CheckCircleIcon, XCircleIcon } from "lucide-react"
import ActionButton from "../../presentation/component/button/ActionButton"
import { ActionButtonVariant } from "../../presentation/models/enum/ActionButtonVariant"

const ChangePassword: React.FC = () => {
  // Inject services using Dependency Injection
  const franchiseService = container.resolve<AppFranchiseService>("franchiseService")
  const logger = container.resolve<Logger>("logger")
  const navigate = useNavigate()

  // State for form inputs and error handling
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const passwordsMatch =
    newPassword && confirmPassword && newPassword === confirmPassword
  const { clearUserCode } = useAuth()
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  /**
   * Handles password change request.
   */
  const handlePasswordChange = async () => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,24}$/

    setLoading(true)
    setError("")
    clearUserCode()

    // Check if all fields are filled
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("All fields are required.")
      setLoading(false)
      return
    }

    // Validate new password
    if (!passwordRegex.test(newPassword)) {
      setError( "Password must be 8–24 characters long and include uppercase, lowercase, number, and special character.")
      setLoading(false)
      return
    }

    // Check password match
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.")
      setLoading(false)
      return
    }

    // Validate new password
    const passwordError = validatePassword(newPassword)
    if (passwordError) {
      setError(passwordError)
      setLoading(false)
      return
    }

    try {
      const response = await franchiseService.changePassword(
        currentPassword,
        newPassword
      )

      alert(response.message)
      navigate("/login")
    } catch (err: any) {
      logger.error("Error changing password:", err)
      setError(err.response?.data?.message || "Failed to change password.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center sm:py-12">
      <div className="p-10 xs:p-0 mx-auto md:w-full md:max-w-lg">
        <div className="bg-white shadow w-full rounded-lg divide-y divide-gray-200">
          <div className="px-5 py-7">
            <h2
              data-automation-id="changepassword-page-changepassword-text"
              id="changepassword-page-changepassword-text"
              className="text-2xl font-semibold text-gray-600 mb-5 text-center"
            >
              Change Password
            </h2>
            {error && (
              <p
                data-automation-id="changepassword-error-update-password-message"
                id="changepassword-error-update-password-message"
                className="text-red-500 mb-3"
              >
                {error}
              </p>
            )}
            <div>
              <label
                data-automation-id="changepassword-current-password-label"
                id="changepassword-current-password-label"
                className="font-semibold text-sm text-gray-600 pb-1 block"
              >
                Current Password
              </label>
              <input
                data-automation-id="changepassword-inputbox-current-password-input"
                id="changepassword-inputbox-current-password-input"
                type={TYPES.PASSWORD}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={loading}
                className="border rounded-lg px-3 py-2 mt-1 mb-5 text-sm w-full"
                placeholder="Enter your current password"
              />
            </div>
            <div>
              <label
                data-automation-id="changepassword-new-password-label"
                id="changepassword-new-password-label"
                className="font-semibold text-sm text-gray-600 pb-1 block"
              >
                New Password
              </label>
              <input
                data-automation-id="changepassword-inputbox-new-password-input"
                id="changepassword-inputbox-new-password-input"
                type={TYPES.PASSWORD}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
                className="border rounded-lg px-3 py-2 mt-1 mb-5 text-sm w-full"
                placeholder="Enter your new password"
              />
            </div>
            <div className="relative">
              <label
                className="font-semibold text-sm text-gray-600 pb-1 block"
                htmlFor="changepassword-inputbox-confirm-password-input"
              >
                Re-type New Password
              </label>
              <input
                data-automation-id="changepassword-inputbox-confirm-password-input"
                id="changepassword-inputbox-confirm-password-input"
                type={TYPES.PASSWORD}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                className={`border rounded-lg px-3 py-2 mt-1 mb-5 text-sm w-full ${confirmPassword && !passwordsMatch ? "border-red-500" : ""
                  }`}
                placeholder="Re-type your new password"
              />
              {/* Match indicator icon */}
              {confirmPassword && (
                <div className="absolute right-3 top-9">
                  {passwordsMatch ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-red-500" />
                  )}
                </div>
              )}
            </div>
            <ActionButton
              variant={ActionButtonVariant.PRIMARY}
              data-automation-id="changepassword-send-new-password-email-button"
              id="changepassword-send-new-password-email-button"
              onClick={handlePasswordChange}
              label={loading ? "Loading..." : "Change Password"}
              disabled={loading || !passwordsMatch}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChangePassword